import React from 'react';

import type { MessageDescriptor } from 'react-intl-next';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import ChangeToneIcon from '@atlaskit/icon/core/microphone';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import {
	type ChangeToneBusinessCasualIntentSchema,
	type ChangeToneEducationalIntentSchema,
	type ChangeToneEmpatheticIntentSchema,
	type ChangeToneNeutralIntentSchema,
	type ChangeToneProfessionalIntentSchema,
} from '../../../../../types/prompt-request-types';
import { ToneCasualIcon } from '../../../../icons/prompt/toneCasual';
import { ToneEducationalIcon } from '../../../../icons/prompt/toneEducational';
import { ToneEmpatheticIcon } from '../../../../icons/prompt/toneEmpathetic';
import { ToneNeutralIcon } from '../../../../icons/prompt/toneNeutral';
import { ToneProfessionalIcon } from '../../../../icons/prompt/toneProfessional';
import type { ConvoAiChangeToneFeatureIds } from '../../../../utils/request/types';
import { createInsertBelow, createReplace } from '../../actions/create-actions';
import { CONFIG_ITEM_KEYS } from '../../config-item-keys';
import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemWithOptions,
} from '../../config-items';

function createChangeToneOption({
	key,
	title,
	description,
	intentSchemaId,
	featureId,
	icon,
	shortTitle,
	adfPrompt,
}: Pick<EditorPluginAIConfigItem, 'key' | 'title' | 'description' | 'icon'> & {
	adfPrompt: boolean;
	featureId: ConvoAiChangeToneFeatureIds;
	intentSchemaId:
		| ChangeToneBusinessCasualIntentSchema
		| ChangeToneEducationalIntentSchema
		| ChangeToneEmpatheticIntentSchema
		| ChangeToneNeutralIntentSchema
		| ChangeToneProfessionalIntentSchema;
	shortTitle: MessageDescriptor;
}): EditorPluginAIConfigItem {
	return {
		key,
		title,
		description,
		icon,
		showInRefineDropdown: true,
		intentSchemaId,
		featureId,
		adfPrompt,
		requiresTextualContent: adfPrompt,
		nestingConfig: {
			id: 'change-tone-to',
			parentTestId: 'change-tone-to',
			parentTitle: aiConfigItemMessages.changeToneNestingParentTitlePostGA,
			shortTitle,
			icon: () => <ChangeToneIcon label="" spacing="spacious" />,
		},
		actions: {
			empty: [
				createInsertBelow({ appearance: 'secondary' }),
				createReplace({ appearance: 'primary' }),
			],
			selection: [
				createInsertBelow({ appearance: 'secondary' }),
				createReplace({ appearance: 'primary' }),
			],
		},
		getBackendModel: () => {
			return 'assistance-service';
		},
	};
}

export const changeToneToNeutral = () => {
	let featureId: ConvoAiChangeToneFeatureIds = 'change_tone_neutral';
	let adfPrompt = false;
	if (expValEqualsNoExposure('platform_editor_ai_ct_sg_adf_streaming', 'isEnabled', true)) {
		featureId = 'change_tone_neutral_adf';
		adfPrompt = true;
	}

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_NEUTRAL,
		title: aiConfigItemMessages.changeToneNeutralToneTitle,
		shortTitle: aiConfigItemMessages.changeToneNeutralToneShortTitle,
		description: aiConfigItemMessages.changeToneNeutralToneDescription,
		intentSchemaId: 'change_tone_neutral_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneNeutralIcon />,
	});
};

export const changeToneToNeutralWithOptions: () => EditorPluginAIConfigItemWithOptions = () => ({
	config: changeToneToNeutral(),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToProfessional = () => {
	let featureId: ConvoAiChangeToneFeatureIds = 'change_tone_professional';
	let adfPrompt = false;

	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'change_tone_professional_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure(
			'platform_editor_ai_non_iw_adf_streaming',
			'cohort',
			'adf_gemini25flash',
		) ||
		fg('platform_editor_ai_updated_adf_prompts_v111')
	) {
		featureId = 'change_tone_professional_adf_v111';
		adfPrompt = true;
	} else if (fg('platform_editor_ai_updated_adf_prompts')) {
		featureId = 'change_tone_professional_adf_v11';
		adfPrompt = true;
	} else if (expValEqualsNoExposure('platform_editor_ai_ct_sg_adf_streaming', 'isEnabled', true)) {
		featureId = 'change_tone_professional_adf';
		adfPrompt = true;
	}

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_PROFESSIONAL,
		title: aiConfigItemMessages.changeToneProfessionalToneTitle,
		shortTitle: aiConfigItemMessages.changeToneProfessionalToneShortTitle,
		description: aiConfigItemMessages.changeToneProfessionalToneDescription,
		intentSchemaId: 'change_tone_professional_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneProfessionalIcon />,
	});
};

export const changeToneToProfessionalWithOptions: () => EditorPluginAIConfigItemWithOptions =
	() => ({
		config: changeToneToProfessional(),
		triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
	});

export const changeToneToCasual = () => {
	let featureId: ConvoAiChangeToneFeatureIds = 'change_tone_casual';
	let adfPrompt = false;
	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'change_tone_casual_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure(
			'platform_editor_ai_non_iw_adf_streaming',
			'cohort',
			'adf_gemini25flash',
		) ||
		fg('platform_editor_ai_updated_adf_prompts_v111')
	) {
		featureId = 'change_tone_casual_adf_v111';
		adfPrompt = true;
	} else if (fg('platform_editor_ai_updated_adf_prompts')) {
		featureId = 'change_tone_casual_adf_v11';
		adfPrompt = true;
	} else if (expValEqualsNoExposure('platform_editor_ai_ct_sg_adf_streaming', 'isEnabled', true)) {
		featureId = 'change_tone_casual_adf';
		adfPrompt = true;
	}

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_CASUAL,
		title: aiConfigItemMessages.changeToneCasualToneTitle,
		shortTitle: aiConfigItemMessages.changeToneCasualToneShortTitle,
		description: aiConfigItemMessages.changeToneCasualToneDescription,
		intentSchemaId: 'change_tone_business_casual_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneCasualIcon />,
	});
};

export const changeToneToCasualWithOptions: () => EditorPluginAIConfigItemWithOptions = () => ({
	config: changeToneToCasual(),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});

export const changeToneToEducational = () => {
	let featureId: ConvoAiChangeToneFeatureIds = 'change_tone_educational';
	let adfPrompt = false;
	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'change_tone_educational_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure(
			'platform_editor_ai_non_iw_adf_streaming',
			'cohort',
			'adf_gemini25flash',
		) ||
		expValEqualsNoExposure('platform_editor_ai_ct_sg_adf_streaming', 'isEnabled', true)
	) {
		featureId = 'change_tone_educational_adf';
		adfPrompt = true;
	}

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EDUCATIONAL,
		title: aiConfigItemMessages.changeToneEducationalToneTitle,
		shortTitle: aiConfigItemMessages.changeToneEducationalToneShortTitle,
		description: aiConfigItemMessages.changeToneEducationalToneDescription,
		intentSchemaId: 'change_tone_educational_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneEducationalIcon />,
	});
};

export const changeToneToEducationalWithOptions: () => EditorPluginAIConfigItemWithOptions =
	() => ({
		config: changeToneToEducational(),
		triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
	});

export const changeToneToEmpathetic = () => {
	let featureId: ConvoAiChangeToneFeatureIds = 'change_tone_empathetic';
	let adfPrompt = false;
	if (
		expValEqualsNoExposure('platform_editor_ai_non_iw_adf_streaming', 'cohort', 'adf_gpt41mini')
	) {
		featureId = 'change_tone_empathetic_adf_gpt41mini';
		adfPrompt = true;
	} else if (
		expValEqualsNoExposure(
			'platform_editor_ai_non_iw_adf_streaming',
			'cohort',
			'adf_gemini25flash',
		) ||
		fg('platform_editor_ai_updated_adf_prompts_v111')
	) {
		featureId = 'change_tone_empathetic_adf_v111';
		adfPrompt = true;
	} else if (fg('platform_editor_ai_updated_adf_prompts')) {
		featureId = 'change_tone_empathetic_adf_v11';
		adfPrompt = true;
	} else if (expValEqualsNoExposure('platform_editor_ai_ct_sg_adf_streaming', 'isEnabled', true)) {
		featureId = 'change_tone_empathetic_adf';
		adfPrompt = true;
	}

	return createChangeToneOption({
		key: CONFIG_ITEM_KEYS.CHANGE_TONE_TO_EMPATHETIC,
		title: aiConfigItemMessages.changeToneEmpatheticToneTitle,
		shortTitle: aiConfigItemMessages.changeToneEmpatheticToneShortTitle,
		description: aiConfigItemMessages.changeToneEmpatheticToneDescription,
		intentSchemaId: 'change_tone_empathetic_intent_schema.json',
		featureId,
		adfPrompt,
		icon: () => <ToneEmpatheticIcon />,
	});
};

export const changeToneToEmpatheticWithOptions: () => EditorPluginAIConfigItemWithOptions = () => ({
	config: changeToneToEmpathetic(),
	triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
});
